﻿import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  Paper,
} from "@mui/material";
import {
  selectSchemaDocuments,
  setSelectedDocumentInfo,
  selectSelectedConnection,
  selectSchemaDocumentAdded,
  setSchemaDocumentAdded,
  updateSchema,
  selectSchemaCollectionAdded,
} from "@/features/connections/integrationSlice";
import { useDispatch, useSelector } from "react-redux";
import axios from "@/features/axiosHelper";
import { useGridApiRef } from "@mui/x-data-grid";
import { createAlert } from "@/features/homepage/alertSliceList";
import ShepButton from "@/components/GenericComponents/ShepButton";
import ShepTable from "@/components/GenericComponents/ShepTable";

const SchemaCollectionGrid = () => {
  const dispatch = useDispatch();
  const selectedConnection = useSelector(selectSelectedConnection);
  const [addedSchemaDocuments, setAddedSchemaDocuments] = useState([]);
  const schemaDocumentAdded = useSelector(selectSchemaDocumentAdded);
  const gridRef = useGridApiRef();

  const [showTransactionTypeScreen, setShowTransactionTypeScreen] =
    useState(false);

  const [showArtifactTypeEditBlock, setShowArtifactTypeEditBlock] =
    useState(false);

  const [artifactTypes, setArtifactTypes] = useState([]); //to keep the artifactTypes for outbound transactions
  const [rowToSetArtifactType, setRowToSetArtifactType] = useState([]);
  const [selectedArtifactType, setSelectedArtifactType] = useState(""); //to store selected artifact type

  const [schemaVariants, setSchemaVariants] = useState([]); //specifically to set packing structure for 856 - Shipment Notice
  const [schemaVariantsEnum, setSchemaVariantsEnum] = useState({});
  const [selectedVariant, setSelectedVariant] = useState(null);

  const schemaId = useSelector(selectSchemaCollectionAdded);
  const handleArtifactChange = (event) => {
    setSelectedArtifactType(event.target.value);
  };
  const handleVariantChange = (event) => {
    setSelectedVariant(event.target.value);
  };

  useEffect(() => {
    getSchemaCollectionDocuments();
  }, []);

  useEffect(() => {
    if (schemaDocumentAdded) {
      getSchemaCollectionDocuments();
      dispatch(setSchemaDocumentAdded({ schemaDocumentAdded: false }));
    }
  }, [schemaDocumentAdded]);

  const getSchemaCollectionDocuments = () => {
    axios
      .get("/connectionartifactschema", {
        params: {
          connectionId: selectedConnection.ConnectionId,
          isMapping: false,
        },
      })
      .then((response) => {
        setAddedSchemaDocuments(response.data);
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "result",
            message:
              "Error encountered when fetching the selected documents for the schema",
            buttonLabel: "Close",
          })
        );
        console.error("Error fetching data: ", error);
      });
  };

  const columns = [
    {
      field: "Name",
      headerName: "Name",
      width: 200,
    },
    {
      field: "SchemaName",
      headerName: "Schema Name",
      width: 400,
    },
    {
      field: "Document",
      headerName: "Document",
      width: 200,
    },
    {
      field: "Standard",
      headerName: "Standard",
      width: 200,
    },
    {
      field: "Direction",
      headerName: "Direction",
      width: 200,
    },
    {
      field: "DocumentDescription",
      headerName: "Type",
      width: 200,
    },
    {
      field: "VariantName",
      headerName: "Variant",
      width: 200,
    },
    {
      field: "add",
      type: "actions",
      sortable: false,
      width: 300,
      renderCell: (params) => {
        const value = params.row;
        const desc = params.row.DocumentDescription;
        return (
          <>
            {desc === "" && params.row.Direction === "Outbound" && (
              <ShepButton
                variant="primary"
                onClick={() => handleAvailableTransactionTypes(params.row)}
              >
                Add Type
              </ShepButton>
            )}
            {desc !== "" && params.row.Direction === "Outbound" && (
              <ShepButton
                variant="secondary"
                onClick={() => handleAvailableTransactionTypes(params.row)}
              >
                Edit Type
              </ShepButton>
            )}
          </>
        );
      },
    },
  ];

  const handleAvailableTransactionTypes = (row) => {
    if (row !== null || row !== undefined) {
      setSelectedArtifactType(row.ArtifactType);

      if (row.Direction === "Outbound") {
        axios
          .get("/connectionartifactschema/get-artifact-types", {
            params: {
              transactionType: row.Document,
            },
          })
          .then((response) => {
            setArtifactTypes(response.data);
            setShowArtifactTypeEditBlock(true);
            setRowToSetArtifactType(row);
            fetchSchemaVariantsOptions(row);
          });
      }
    }
  };
  //SHEP-853
  const fetchSchemaVariantsOptions = (row) => {
    axios
      .get("/connectionartifactschema/artifact-schema-variants", {
        params: {
              transactionType: row.Document,
              endpointSchemaId: row.EndpointSchemaId,
        },
      })
      .then((response) => {
        setSchemaVariants(response.data.VariantList); //will return either a list with values or null if there is no variants
        setSchemaVariantsEnum(response.data.VariantEnum);
        if (row.VariantName !== null && row.VariantName !== undefined) {
          if (row.Document === "O856") {
            const value = response.data.VariantEnum[row.VariantName];
            setSelectedVariant(value);
          }
        } else {
          setSelectedVariant(null);
        }
      });
  };

  const validateRemove = (e) => {
    const map = gridRef?.current.getSelectedRows();
    const schemaCollectionDocuments = Array.from(map, ([name, value]) => ({
      ...value,
    }));

    if (schemaCollectionDocuments.length === 0) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          message: "Please select a document to delete!",
          buttonLabel: "Close",
        })
      );
    } else {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          title: "Remove Schema Document",
          message: "Are you sure you want to remove the schema document?",
          buttonLabel: "Remove",
          onButtonClick: () => handleRemove(),
          buttonLabel2: "Cancel",
        })
      );
    }
  };

  const handleRemove = () => {
    const map = gridRef?.current.getSelectedRows();
    const schemaCollectionDocuments = Array.from(map, ([name, value]) => ({
      ...value,
    }));

    if (schemaCollectionDocuments.length > 0) {
      const connectionId = schemaCollectionDocuments[0].ConnectionId;
      const connectionArtifactSchemaIds = schemaCollectionDocuments.map(
        (x) => x.ConnectionArtifactSchemaId
      );
      const connArtifactSchemaIds = JSON.stringify(connectionArtifactSchemaIds);
      axios
        .delete("/connectionartifactschema", {
          params: {
            connectionId: connectionId,
            connArtifactSchemaIds: connArtifactSchemaIds,
          },
        })
        .then((response) => {
          if (response.data.error === false) {
            dispatch(
              createAlert({
                status: "Success",
                alertType: "result",
                title: "Delete Successful!",
                message: "Deleted the schema document successfully",
                buttonLabel: "Close",
              })
            );
            dispatch(setSchemaDocumentAdded({ schemaDocumentAdded: true }));
            var schemaCollectionId = schemaId.SchemaCollectionId;

            dispatch(
              updateSchema({
                schemaCollectionId: schemaCollectionId,
                connectionId: connectionId,
              })
            );

            // heap.io
            window.heap.track("Schema Collection - Remove Document");
          } else if (response.data.error === true) {
            dispatch(
              createAlert({
                status: "Error",
                alertType: "result",
                title: "Delete Unsuccessful!",
                message: "Error in removing schema documents",
                buttonLabel: "Close",
              })
            );
          }
        })
        .catch((error) => {
          dispatch(
            createAlert({
              status: "Error",
              alertType: "result",
              title: "Delete Unsuccessful!",
              message: "Error in removing schema documents",
              buttonLabel: "Close",
            })
          );
        });
    } else {
      dispatch(
        createAlert({
          status: "Error",
          alertType: "result",
          title: "Delete Unsuccessful!",
          message: "No record selected",
          buttonLabel: "Close",
        })
      );
    }
  };

  const handleCancelClick = () => {
    setArtifactTypes([]);
    setShowArtifactTypeEditBlock(false);
    setRowToSetArtifactType([]);
    setSelectedArtifactType("");
  };

  const handleSaveClick = () => {
    if (
      selectedArtifactType === "" ||
      selectedArtifactType === null ||
      selectedArtifactType === undefined
    ) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          message: "Please select a Type!",
          buttonLabel: "Close",
        })
      );
      return;
    }
    if (
      schemaVariants &&
      (selectedVariant === "" ||
        selectedVariant === null ||
        selectedVariant === undefined)
    ) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          message: "Please select a Variant Type!",
          buttonLabel: "Close",
        })
      );
      return;
    }
    const variantName =
      selectedVariant == null
        ? ""
        : Object.keys(schemaVariantsEnum).filter(
            (x) => schemaVariantsEnum[x] === selectedVariant
          )[0];
    axios
      .post(
        "/connectionartifactschema/save-artifact-type",
        {
          ConnectionId: rowToSetArtifactType.ConnectionId,
          ArtifactType: selectedArtifactType,
          ConnectionArtifactSchemaId:
            rowToSetArtifactType.ConnectionArtifactSchemaId,
          VariantName: variantName,
        },
        null
      )
      .then((response) => {
        dispatch(
          createAlert({
            status: "Success",
            alertType: "result",
            message: "The type was added successfully.",
            buttonLabel: "Close",
          })
        );
        getSchemaCollectionDocuments();
        setShowArtifactTypeEditBlock(false);
        setRowToSetArtifactType([]);
        setSelectedArtifactType("");
      })
      .catch((error) => {
        let err = error.response.data;
        if (err.success === false) {
          dispatch(
            createAlert({
              status: "Error",
              alertType: "result",
              message: err.message,
              buttonLabel: "Close",
            })
          );
        }
        getSchemaCollectionDocuments();
        setShowArtifactTypeEditBlock(false);
        setRowToSetArtifactType([]);
        setSelectedArtifactType("");
      });
  };

  // SHEP-416 filter selected documents by description + currently selected
  const [searchValue, setSearchValue] = useState("");
  const filteredAddedSchemaDocuments = addedSchemaDocuments.filter((doc) =>
    doc.SchemaName.includes(searchValue)
  );
  return (
    <Container className="content-container" style={{ padding: "0px" }}>
      <ShepTable
        apiRef={gridRef}
        tableTitle="Selected Documents"
        dataRows={filteredAddedSchemaDocuments}
        columnTitles={columns}
        getRowId={(row) => row.EndpointSchemaId}
        checkboxesEnabled={true}
        searchValue={searchValue}
        handleSearchChange={(e) => setSearchValue(e.target.value)}
        tableActions={[
          { label: "Remove", onClick: validateRemove, variant: "danger" },
        ]}
        emptyMessage={{
          title: "No Documents Selected",
          message: "Add a document to display data.",
        }}
      />
      {filteredAddedSchemaDocuments &&
        filteredAddedSchemaDocuments.length > 0 && (
          <>
            {showArtifactTypeEditBlock &&
              artifactTypes &&
              rowToSetArtifactType && (
                <>
                  <Card className="global-settings-card">
                    <Grid
                      container
                      rowSpacing={1}
                      columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                    >
                      <Grid item xs={1.5}></Grid>
                      <Grid item xs={9}>
                        <Typography
                          variant="h3"
                          sx={{ marginBottom: "20px", marginTop: "50px" }}
                        >
                          {rowToSetArtifactType.ArtifactType == 0
                            ? "Add"
                            : "Edit"}{" "}
                          Type - {rowToSetArtifactType.SchemaName}
                        </Typography>
                      </Grid>
                    </Grid>
                    <br></br>
                    <br></br>
                    <Grid
                      container
                      rowSpacing={1}
                      columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                    >
                      <Grid item xs={1.5}></Grid>
                      <Grid item xs={2}>
                        <Typography>
                          Select Type{" "}
                          <span
                            style={{
                              color: "red",
                              fontSize: "1.2rem",
                              fontWeight: "bold",
                            }}
                          >
                            *
                          </span>
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <FormControl>
                          <InputLabel id="artifact-type-label">
                            Select Type
                          </InputLabel>
                          <Select
                            labelId="artifact-type-label"
                            id="artifact-type-select"
                            value={selectedArtifactType}
                            label="Select Artifact Type"
                            onChange={handleArtifactChange}
                            MenuProps={{
                              style: { zIndex: 1500 },
                            }}
                            className="select-component"
                          >
                            {artifactTypes.map((item) => (
                              <MenuItem
                                key={item.ArtifactType}
                                value={item.ArtifactType}
                              >
                                {item.ArtifactName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      {schemaVariants && (
                        <>
                          <Grid item xs={2}>
                            <Typography>
                              Select Variant Type{" "}
                              <span
                                style={{
                                  color: "red",
                                  fontSize: "1.2rem",
                                  fontWeight: "bold",
                                }}
                              >
                                *
                              </span>
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <FormControl>
                              <InputLabel id="artifact-type-label">
                                Select Type
                              </InputLabel>
                              <Select
                                labelId="artifact-type-label"
                                id="artifact-type-select"
                                value={selectedVariant}
                                label="Select Setting Type"
                                onChange={handleVariantChange}
                                MenuProps={{
                                  style: { zIndex: 1500 },
                                }}
                                className="select-component"
                              >
                                {schemaVariants.map((item) => (
                                  <MenuItem
                                    key={item.ArtifactType}
                                    value={item.ArtifactType}
                                    name={item.ArtifactName}
                                  >
                                    {item.ArtifactName}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        </>
                      )}
                    </Grid>
                    <Grid
                      container
                      spacing={2}
                      justifyContent="flex-end"
                      sx={{ marginTop: 4, marginRight: 4 }}
                    >
                      <Grid item>
                        <ShepButton
                          variant="secondary"
                          onClick={handleCancelClick}
                        >
                          Cancel
                        </ShepButton>
                      </Grid>
                      <Grid item>
                        <ShepButton variant="primary" onClick={handleSaveClick}>
                          Save
                        </ShepButton>
                      </Grid>
                    </Grid>
                  </Card>
                </>
              )}
          </>
        )}
    </Container>
  );
};

const UpdateSchemaGrid = ({ onAdd }) => {
  const schemaDocuments = useSelector(selectSchemaDocuments);
  const dispatch = useDispatch();
  const gridRef = useGridApiRef();

  // SHEP-416 filter selected documents by description + currently selected
  const [searchValue, setSearchValue] = useState("");
  const filteredSchemaDocuments = schemaDocuments.filter((doc) =>
    doc.DocumentDescriptipn.includes(searchValue)
  );

  const columns = [
    {
      field: "SchemaCollectionName",
      headerName: "Schema Collection Name",
      width: 400,
    },
    {
      field: "SchemaType",
      headerName: "Schema Type",
      width: 200,
    },
    {
      field: "DocumentName",
      headerName: "Document Name",
      width: 200,
    },
    {
      field: "DocumentDescriptipn",
      headerName: "Document Description",
      width: 500,
    },
  ];

  const handleAllSelectedRows = () => {
    const map = gridRef?.current.getSelectedRows();
    const selectedRows = Array.from(map, ([name, value]) => ({ ...value }));
    dispatch(setSelectedDocumentInfo({ selectedDocumentInfo: selectedRows }));
  };

  return (
    <>
      <Box
        sx={{
          marginTop: "30px",
          marginBottom: "30px",
        }}
      >
        <SchemaCollectionGrid />
      </Box>

      <Box
        sx={{
          marginTop: "25px",
          marginBottom: "30px",
        }}
      >
        <ShepTable
          tableTitle="Documents"
          apiRef={gridRef}
          dataRows={filteredSchemaDocuments}
          searchValue={searchValue}
          handleSearchChange={(e) => {
            setSearchValue(e.target.value);
          }}
          tableActions={[
            {
              label: "Add",
              onClick: onAdd,
            },
          ]}
          columnTitles={columns}
          getRowId={(row) => row.EndpointSchemaId}
          checkboxesEnabled={true}
          enableRowClickSelectionWithCheckboxes={true}
          handleSelectedRowChange={handleAllSelectedRows}
        />
      </Box>
    </>
  );
};
export default UpdateSchemaGrid;
